<script setup lang="ts">
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { TransportType } from '@libs/trip-planner-interfaces';
import { isExperimentAsync } from '@libs/feature-toggle';

import type { AvailableItineraryFlightSegment } from '../../../models';
import ChangeProductLink from '../ChangeProductLink.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import { useTripOverviewPageStore } from '../../../stores';

import FlightProductCard from './FlightProductCard.vue';
import FlightSegmentHeader from './FlightSegmentHeader.vue';
import ExperimentalFlightSegmentHeader from './ExperimentalFlightSegmentHeader.vue';

interface ItineraryFlightSegmentProps {
  segment: AvailableItineraryFlightSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryFlightSegmentProps>();

interface FlightProductCardEmits {
  (event: 'show-details'): void;
  (event: 'show-more-filters'): void;
  (event: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (event: 'select-transport', transportType: TransportType): void;
  (event: 'remove-filter', tagId: string): void;
}

const emit = defineEmits<FlightProductCardEmits>();

const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();
const recipeStore = useRecipeStore();

const isFirstTransportSegment = computed<boolean>(
  () =>
    recipeStore.flightOnlyRecipe[0]?.ingredientId === props.segment.ingredientId
);

const isMobileHeaderExperiment = computedAsync<boolean>(
  () => isExperimentAsync('mobile_flight_header'),
  false
);

const shouldShowExperiment = computed(
  () => isMobileHeaderExperiment.value && browserStore.isSmallerThan600px
);

function removeFlight(): void {
  tripOverviewPageStore.selectTransport(props.segment, TransportType.None);
}
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    :is-experimental-header-enabled="shouldShowExperiment"
    :class="{
      'has-experimental-header': shouldShowExperiment,
      'hide-timeline': isFirstTransportSegment && shouldShowExperiment,
      'flight-selected':
        shouldShowExperiment && props.segment.suggestionType === undefined,
    }"
  >
    <template v-if="shouldShowExperiment" #experimental-header>
      <ExperimentalFlightSegmentHeader
        :segment="props.segment"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @show-more-filters="emit('show-more-filters')"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('show-more-filters');
        "
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>
    <template v-if="!shouldShowExperiment" #dropdown>
      <ItineraryTransportSelector
        :transport-type="TransportType.Flight"
        class="flight-selector"
        :disabled="props.printMode"
        @select-transport="
          tripOverviewPageStore.selectTransport(props.segment, $event)
        "
      />
    </template>
    <template
      v-if="!shouldShowExperiment && !browserStore.isSmallerThan1200px"
      #header
    >
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @show-more-filters="emit('show-more-filters')"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>
    <template
      v-if="!shouldShowExperiment && browserStore.isSmallerThan1200px"
      #large-header
    >
      <FlightSegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        @show-more-filters="emit('show-more-filters')"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @remove-filter="emit('remove-filter', $event)"
      />
    </template>

    <template #content>
      <div class="available-flight-segment-content-wrapper">
        <FlightProductCard
          :product-card-data="props.segment.productCardData"
          :print-mode="props.printMode"
          @show-details="emit('show-details')"
          @remove-flight="removeFlight()"
        />
        <ChangeProductLink
          v-if="!props.printMode"
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-flight-segment-content-wrapper {
  display: flex;
  flex-flow: column;
  gap: var(--spacing-16);

  .change-product-link {
    align-self: flex-end;
  }
}

.has-experimental-header {
  .actions {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-8);
    justify-content: space-between;
    align-items: center;
  }
}
</style>
